import React, { useState, useCallback, useRef } from 'react';
import { SparklesIcon } from './Icons';
import { LoadingSpinner } from './LoadingSpinner';
import AIImageUploadForm from './AIImageUploadForm';
import EditedImageCard from './EditedImageCard';
import ImageModal from './ImageModal';
import './AIImageEditor.css';

/**
 * Convert file to base64
 */
const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const result = reader.result;
            const base64Data = result.split(',')[1];
            resolve({
                data: base64Data,
                mimeType: file.type
            });
        };
        reader.onerror = (error) => reject(error);
    });
};

/**
 * Call Cloud Function to edit image
 */
// Helper function to call the Cloud Function
const editImageViaCloudFunction = async (base64Image, finalPrompt) => {
    const response = await fetch('https://us-central1-sumstore-b483e.cloudfunctions.net/editImage', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            image: base64Image,
            prompt: finalPrompt,
        }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error Response:', errorData);
        throw new Error(`خطأ في الـ API: ${errorData.error || response.statusText}${errorData.details ? '\n' + errorData.details : ''}`);
    }

    return await response.json();
};

const AIImageEditor = ({ onImageGenerated }) => {
    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [editedImageResult, setEditedImageResult] = useState(null);
    const [fashionPose, setFashionPose] = useState('');
    const [fashionBackgroundColor, setFashionBackgroundColor] = useState('Cool Grey');
    const [artisticStyle, setArtisticStyle] = useState('default');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [modalImage, setModalImage] = useState(null);
    const resultsRef = useRef(null);

    const backgroundPrompts = {
        'White': "a seamless, pure white studio background",
        'Warm Beige': "a seamless, warm beige studio background",
        'Cool Grey': "a seamless, gradient cool grey studio background",
        'Soft Pink': "a seamless, soft pastel pink studio background",
        'Sky Blue': "a seamless, light sky blue studio background",
        'Olive Green': "a seamless, muted olive green studio background",
        'Burnt Orange': "a seamless, warm burnt orange studio background",
        'Rich Maroon': "a seamless, rich maroon studio background",
        'Deep Navy': "a seamless, deep navy blue studio background",
        'Charcoal': "a seamless, dark charcoal grey studio background"
    };

    const artisticStyleTemplates = {
        'cinematic': "A stunning, high-fashion product photograph of a garment, gracefully floating or suspended. The composition is centered. Dramatic, cinematic lighting from the upper right creates intense, warm highlights and ethereal, glowing rim light effect that sharply contrasts with cooler shadows. The background is {{BACKGROUND_PLACEHOLDER}}. The overall aesthetic is clean, modern, and elegant, rendered with photorealistic precision, emphasizing the delicate texture and luxurious feel of the fabric, achieved with a shallow depth of field.",
        'ethereal': "A highly detailed, ethereal, high-fashion studio shot of a pristine white mannequin gracefully 'floating' mid-air, wearing an exquisite garment. The entire scene is bathed in soft, diffused, high-key overhead lighting. Surrounding the mannequin are numerous delicate, abstract, petal-like fabric elements, fluttering around. The background is a vast, flowing cascade of smooth fabric that is {{BACKGROUND_PLACEHOLDER}}. The image exudes a sense of purity, lightness, and elegant motion, rendered with hyperrealistic detail.",
        'minimalist': "A high-key, minimalist studio product photograph of a garment, dynamically suspended mid-air as if in motion. The garment exhibits natural folds and creases. The composition features the garment floating against a clean, seamless background which is {{BACKGROUND_PLACEHOLDER}}. Soft, diffused professional studio lighting illuminates the garment from above and slightly in front, creating subtle volumetric shadows. A soft, elongated shadow is cast beneath the garment. Shot with a shallow depth of field, sharp focus, evoking a clean, contemporary aesthetic.",
        'street': "A full-body, high-fashion studio shot of an invisible person or headless mannequin, dynamically posed. The background is a clean, minimalist seamless studio setup which is {{BACKGROUND_PLACEHOLDER}}. Dramatic, high-contrast, directional natural light originates from the upper left, casting intricate, sharp, elongated shadow patterns resembling palm fronds or window blinds across the entire background and subtly onto the subject. The lighting creates strong specular highlights on the fabric. The overall aesthetic is modern, clean, editorial, and sophisticated with a strong focus on light and shadow play."
    };

    const handleImageAdd = useCallback(async (files, onComplete) => {
        if (!files || files.length === 0) {
            onComplete?.();
            return;
        }

        if (files.length > 1) {
            setError("يمكنك إضافة صورة واحدة فقط.");
            onComplete?.();
            return;
        }

        const file = files[0];
        if (file.size > 10 * 1024 * 1024) {
            setError(`حجم الصورة '${file.name}' كبير. اختر صورة أصغر من 10 ميجابايت.`);
            onComplete?.();
            return;
        }

        try {
            setError(null);
            const { data, mimeType } = await fileToBase64(file);
            setImages([{ data, mimeType }]);
            setImagePreviews(prev => {
                prev.forEach(URL.revokeObjectURL);
                return [URL.createObjectURL(file)];
            });
        } catch (err) {
            console.error("Error converting file:", err);
            setError("حصلت مشكلة أثناء تجهيز الصور.");
        } finally {
            onComplete?.();
        }
    }, []);

    const handleImageRemove = useCallback((index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => {
            const urlToRemove = prev[index];
            const newPreviews = prev.filter((_, i) => i !== index);
            if (urlToRemove) URL.revokeObjectURL(urlToRemove);
            return newPreviews;
        });
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        console.log('Form submitted!', { isLoading, imagesLength: images.length, fashionPose });

        if (isLoading || images.length === 0 || !fashionPose) {
            console.log('Submit blocked:', { isLoading, imagesLength: images.length, fashionPose });
            return;
        }

        setIsLoading(true);
        setHasSubmitted(true);
        setError(null);
        setEditedImageResult(null);

        setTimeout(() => {
            resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);

        let prompt;
        const backgroundDescription = backgroundPrompts[fashionBackgroundColor] || backgroundPrompts['Cool Grey'];

        if (artisticStyle === 'default') {
            prompt = `Your task is to perform a professional fashion product photo transformation. 1. **Isolate the Outfit:** From the reference image provided, meticulously isolate the complete outfit. 2. **Remove the Model:** Completely remove every trace of the human model. The final image must contain ONLY the clothing on a perfectly invisible mannequin. 3. **Re-render on Invisible Mannequin:** Re-render the isolated outfit as a hyperrealistic 3D object, worn by an invisible mannequin in the following pose: "${fashionPose}". 4. **CRITICAL for Headwear:** If the garment is a hijab, reconstruct the inner lining to look natural on an invisible head. 5. **Set the Scene:** Place the outfit in a clean, minimalist scene with this background: ${backgroundDescription}. Use soft, diffused studio lighting. 6. **Final Aesthetic:** The final image must have a high-fashion, sophisticated, and ethereal editorial photography aesthetic.`;
        } else {
            const template = artisticStyleTemplates[artisticStyle];
            prompt = template.replace('{{BACKGROUND_PLACEHOLDER}}', backgroundDescription);
        }

        console.log('Calling Cloud Function...', { prompt: prompt.substring(0, 100) });

        try {
            const result = await editImageViaCloudFunction(images[0], prompt);
            console.log('Cloud Function result:', result);
            setEditedImageResult(result);

            // Callback to parent component with the generated image
            if (onImageGenerated && result.newImage) {
                const editedImageUrl = `data:${result.newImage.mimeType};base64,${result.newImage.data}`;
                onImageGenerated(editedImageUrl);
            }
        } catch (err) {
            console.error('Error calling Cloud Function:', err);
            setError('للأسف حصلت مشكلة. حاول مرة أخرى.');
        } finally {
            setIsLoading(false);
        }
    }, [images, isLoading, fashionPose, fashionBackgroundColor, artisticStyle, onImageGenerated, backgroundPrompts, artisticStyleTemplates]);

    const renderResults = () => {
        if (isLoading) {
            return (
                <div className="flex justify-center items-center h-full min-h-[400px]">
                    <LoadingSpinner />
                </div>
            );
        }

        if (error) {
            return (
                <div className="mt-12 text-center text-red-800 bg-red-200/50 p-4 rounded-lg animate-fade-in-up">
                    <p>{error}</p>
                </div>
            );
        }

        if (!hasSubmitted) {
            return (
                <div className="flex flex-col justify-center items-center h-full min-h-[400px] text-center text-stone-500 p-8 border-2 border-dashed border-stone-300 rounded-2xl animate-fade-in-up">
                    <SparklesIcon className="w-16 h-16 mb-4 text-stone-400" />
                    <h2 className="text-2xl font-bold text-stone-700 mb-2">حوّل صور ملابسك لاحترافية</h2>
                    <p>ارفع صورة لقطعة ملابس، اختر الوضعية، والذكاء الاصطناعي سيعرضها على موديل خفي في استوديو احترافي.</p>
                </div>
            );
        }

        if (!editedImageResult) {
            return (
                <div className="flex flex-col justify-center items-center h-full min-h-[400px] text-center text-stone-500 p-8 animate-fade-in-up">
                    <h2 className="text-2xl font-bold text-stone-700 mb-2">لم تظهر نتيجة</h2>
                    <p>لم يتمكن الذكاء الاصطناعي من تحليل الصورة. جرب صورة أخرى.</p>
                </div>
            );
        }

        const editedImageUrl = `data:${editedImageResult.newImage.mimeType};base64,${editedImageResult.newImage.data}`;

        return (
            <div className="animate-fade-in-up">
                <EditedImageCard
                    originalImage={imagePreviews[0]}
                    editedImage={editedImageUrl}
                    responseText={editedImageResult.responseText}
                    onImageClick={setModalImage}
                />
            </div>
        );
    };

    return (
        <div className="ai-image-editor-container">
            <div className="lg:grid lg:grid-cols-5 lg:gap-8">
                {/* Left Side - Form */}
                <div className="lg:col-span-2 lg:sticky lg:top-8 self-start lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto custom-scrollbar">
                    <div className="bg-[rgba(var(--color-surface-rgb),0.3)] backdrop-blur-md border border-[rgba(var(--color-border-rgb),0.3)] rounded-2xl p-6 shadow-2xl shadow-stone-900/10">
                        <div className="flex items-center justify-center gap-2 pb-3 mb-6 border-b-2 border-red-800">
                            <SparklesIcon className="w-5 h-5 text-red-800" />
                            <h2 className="text-md font-bold text-red-800">تعديل الصور بالذكاء الاصطناعي</h2>
                        </div>

                        <AIImageUploadForm
                            handleSubmit={handleSubmit}
                            isLoading={isLoading}
                            imagePreviews={imagePreviews}
                            handleImageAdd={handleImageAdd}
                            handleImageRemove={handleImageRemove}
                            fashionPose={fashionPose}
                            setFashionPose={setFashionPose}
                            fashionBackgroundColor={fashionBackgroundColor}
                            setFashionBackgroundColor={setFashionBackgroundColor}
                            artisticStyle={artisticStyle}
                            setArtisticStyle={setArtisticStyle}
                        />
                    </div>
                </div>

                {/* Right Side - Results */}
                <div className="lg:col-span-3 mt-12 lg:mt-0" ref={resultsRef}>
                    {renderResults()}
                </div>
            </div>

            {/* Image Modal */}
            {modalImage && <ImageModal src={modalImage} onClose={() => setModalImage(null)} />}
        </div>
    );
};

export default AIImageEditor;
